import { useEffect } from 'react';
import { useAuth } from '@/components/AuthProvider';
import { usePushNotifications, isPushDisabledByUser } from '@/hooks/usePushNotifications';
import { useAppBadge } from '@/hooks/useAppBadge';

/**
 * Silently initializes push notifications when a user is logged in.
 * - Registers the FCM token with the backend if permission is already granted.
 * - Listens for foreground messages (shows toast via the hook).
 * - Does NOT auto-prompt — users enable notifications explicitly from settings.
 * - Honors explicit user disable (via "Disable Notifications" button) — won't silently
 *   re-register a token the user just removed. The disable flag is per-user so shared
 *   computers don't leak one user's preference to another.
 */
export function PushNotificationManager() {
  const { user } = useAuth();
  const { permission, token, requestPermission, isSupported } = usePushNotifications();

  // Sync unread conversation count to the OS app badge (Windows taskbar / mobile home screen)
  useAppBadge();

  useEffect(() => {
    // Only run if user is logged in, browser supports push, and permission already granted
    if (!user || !isSupported || permission !== 'granted') return;

    // Honor explicit user disable. Without this guard, clicking "Disable Notifications"
    // would be undone within milliseconds: the unregister flow clears the local token,
    // this effect re-fires (token changed), sees `permission === 'granted' && !token`,
    // and silently re-registers. The flag (set by unregister, cleared by requestPermission)
    // distinguishes "token missing because user just disabled" from "token missing because
    // user just logged in" — only the latter should auto-register.
    if (isPushDisabledByUser(user.id)) return;

    // If permission is granted but no token stored, register silently.
    // One attempt per browser session: when FCM's push service is unreachable
    // (e.g. "Registration failed - push service error"), every re-render used to
    // retry and spam a failure toast infinitely. Silent mode + this marker means
    // background failures are logged once and never toasted; the user can still
    // retry explicitly from Settings → Notifications.
    if (!token && sessionStorage.getItem('fcm_auto_register_attempted') !== 'true') {
      sessionStorage.setItem('fcm_auto_register_attempted', 'true');
      requestPermission({ silent: true });
    }
  }, [user, isSupported, permission, token, requestPermission]);

  return null;
}
